import React, { lazy, Suspense, useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { addDays } from "date-fns";

import SEO from "../components/SEO";
import { isProductionHost } from "../utils/isProductionHost";
import useQuoteFormState from "./quotes1/components/useQuoteFormState.js";
import { Helmet } from "react-helmet-async";
import {
  Users,
  ShieldCheck,
  Search,
  Activity,
  Accessibility,
  Luggage,
  ChevronLeft,
  ChevronRight,
  Star,
  Circle,
  CheckCircle,
  MessageCircle,
  BadgeCheck,
  PlusSquare,
} from "lucide-react";

// Working Partners logos
import partner1 from "../assets/partners/img1.webp";
import partner2 from "../assets/partners/img2.webp";
import partner3 from "../assets/partners/img3.webp";
import partner4 from "../assets/partners/img4.webp";
import partner5 from "../assets/partners/img5.webp";
import partner6 from "../assets/partners/img6.webp";
import partner7 from "../assets/partners/img7.webp";
import partner8 from "../assets/partners/img8.webp";
import partner9 from "../assets/partners/img9.webp";
import partner10 from "../assets/partners/img10.webp";
import partner11 from "../assets/partners/img11.webp";
import partner12 from "../assets/partners/img12.webp";
import partner13 from "../assets/partners/img13.webp";
import partner14 from "../assets/partners/img14.webp";
import partner15 from "../assets/partners/img15.webp";
import partner16 from "../assets/partners/img16.webp";
import partner17 from "../assets/partners/img17.webp";
import partner18 from "../assets/partners/img18.webp";
import partner19 from "../assets/partners/img19.webp";
import partner20 from "../assets/partners/img20.webp";
import partner21 from "../assets/partners/img21.webp";
import partner22 from "../assets/partners/img22.webp";
import Testimonials from "../components/Testimonials";
import ScrollToTopButton from "../components/ScrollToTopButton";
import img86 from "../assets/img86.webp";
import img87 from "../assets/img87.webp";
import BlogSection from "../components/BlogSection";

// Only the (initially hidden) visitor-insurance banner needs the MUI date
// picker, so keep it out of the homepage's initial JavaScript.
const DateField = lazy(() => import("./quotes1/components/DateField.jsx"));

// date-fns `format` pulls the full formatter/locale set (~100KB) into the
// homepage bundle, and the hero form only needs yyyy-MM-dd on submit.
function formatISODate(date) {
  if (!date) return "";
  const dt = new Date(date);
  if (Number.isNaN(dt.getTime())) return "";
  const month = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${dt.getFullYear()}-${month}-${day}`;
}

function HeroDateField({ id, value, onChange, minDate, maxDate, openId, setOpenId }) {
  const label = formatISODate(value);
  const className =
    "h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-left text-sm text-[#444E59] outline-none transition focus:border-[#20a7df] focus:ring-1 focus:ring-[#20a7df]";

  if (!openId) {
    return (
      <button type="button" id={id} onClick={() => setOpenId(id)} className={className}>
        {label}
      </button>
    );
  }

  return (
    <Suspense fallback={<span className={`${className} inline-flex items-center`}>{label}</span>}>
      <DateField
        id={id}
        value={value}
        onChange={onChange}
        minDate={minDate}
        maxDate={maxDate}
        autoOpen={openId === id}
      />
    </Suspense>
  );
}



function InsuranceCard({ card, animationStyle }) {
  const Icon = card.icon;
  const [isFlipped, setIsFlipped] = useState(false);

  const toggleCard = () => {
    setIsFlipped((current) => !current);
  };

  return (
    <article
      style={animationStyle}
      className="group h-[440px] w-full cursor-pointer [perspective:1200px] sm:h-[430px]"
      role="button"
      tabIndex={0}
      aria-label={`${card.title}. Tap or click to view details.`}
      aria-pressed={isFlipped}
      onClick={toggleCard}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          toggleCard();
        }
      }}
    >
      <div
        className={`
          relative h-full w-full
          [transform-style:preserve-3d]
          transition-transform duration-700 ease-in-out
          lg:group-hover:[transform:rotateY(180deg)]
          ${isFlipped ? "[transform:rotateY(180deg)]" : ""}
        `}
      >
        {/* FRONT SIDE: TITLE, EXPLANATION AND PRICE */}
        <div className="absolute inset-0 flex h-full w-full flex-col rounded-[22px] bg-gradient-to-br from-[#49628b] to-[#20a7df] p-6 text-center text-white shadow-[0_18px_42px_rgba(73,98,139,0.28)] [backface-visibility:hidden] sm:p-7 sm:text-left">
          <div className="mx-auto flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] bg-white/15 text-white sm:mx-0">
            <Icon size={23} strokeWidth={2.2} aria-hidden="true" />
          </div>

          <h3 className="mt-6 text-2xl font-bold leading-tight">
            {card.title}
          </h3>

          <p className="mt-5 flex-1 text-[14px] leading-6 text-white/90">
            {card.explanation}
          </p>

          <div className="mt-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.13em] text-white/75">
              {card.rateLabel}
            </p>

            <p className="mt-1 text-3xl font-extrabold leading-none text-white">
              {card.rate}
            </p>
          </div>
        </div>

        {/* BACK SIDE: DISCLAIMER AND CTA */}
        <div className="absolute inset-0 flex h-full w-full [transform:rotateY(180deg)] flex-col rounded-[22px] bg-gradient-to-br from-[#49628b] to-[#20a7df] p-6 text-center text-white shadow-[0_18px_42px_rgba(73,98,139,0.28)] [backface-visibility:hidden] sm:p-7 sm:text-left">
          <div className="mx-auto flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] bg-white/15 text-white sm:mx-0">
            <Icon size={23} strokeWidth={2.2} aria-hidden="true" />
          </div>

          <h3 className="mt-6 text-2xl font-bold leading-tight">
            {card.title}
          </h3>

          <p className="mt-5 flex-1 text-[14px] leading-6 text-white/90">
            {card.disclaimer}
          </p>

          <a
            href={card.ctaLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
            className="mt-6 inline-flex min-h-11 w-full items-center justify-center rounded-full bg-white px-5 py-2.5 text-sm font-bold text-[#49628b] shadow-[0_10px_24px_rgba(0,0,0,0.16)] transition hover:-translate-y-0.5 hover:bg-[#E8EEF0] focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            Get a quote
          </a>
        </div>
      </div>
    </article>
  );
}

function Home() {
  const navigate = useNavigate();

  const [activeHeroSlide, setActiveHeroSlide] = useState(0);
  const [isHeroFormActive, setIsHeroFormActive] = useState(false);

  // Shares the exact same state hook and validateForm() rules as the
  // /quote/vtc page (src/pages/quotes1/components/CalculatorForm.jsx), so the
  // hero banner quote form has identical fields (minus Age/Pre-existing
  // Condition, which the banner intentionally omits) and validation.
  const {
    visaType,
    setVisaType,
    insured,
    setInsured,
    coverageOptions,
    coverage,
    setCoverage,
    today,
    startDate,
    setStartDate,
    maxStartDate,
    noOfDays,
    setNoOfDays,
    endDate,
    setEndDate,
    dob1,
    setDob1,
    dob2,
    setDob2,
    minDob,
    manualDaysChangedRef,
  } = useQuoteFormState();

  const [heroErrors, setHeroErrors] = useState({});
  const [openHeroDateId, setOpenHeroDateId] = useState(null);

  const toNoon = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    if (isNaN(dt)) return null;
    return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(), 12, 0, 0, 0);
  };

  const handleLifeInsuranceCta = () => {
    navigate("/intake/life?utm_source=homepage&utm_medium=get-your-quote&utm_campaign=website");
  };

  const handleVisitorSuperVisaCta = () => {
    navigate("/quote/vtc?utm_source=homepage&utm_medium=get-your-quote&utm_campaign=website");
  };

  const handleNoOfDaysChange = (event) => {
    const value = event.target.value;
    manualDaysChangedRef.current = true;
    setNoOfDays(value);
    if (value !== "") {
      const days = Number(value);
      setEndDate(toNoon(addDays(startDate, days - 1)));
    }
  };

  const handleNoOfDaysBlur = () => {
    let days = Number(noOfDays);
    if (isNaN(days)) days = 7;
    days = Math.max(7, Math.min(365, days));
    setNoOfDays(days);
    setEndDate(toNoon(addDays(startDate, days - 1)));
  };

  // Mirrors CalculatorForm's validateForm() (src/pages/quotes1/components/CalculatorForm.jsx),
  // minus the Age/Pre-existing Condition checks — the hero banner doesn't expose
  // those fields, so it only validates what it actually collects.
  const validateHeroForm = () => {
    const newErrors = {};

    // --- Required fields ---
    if (!startDate) newErrors.startDate = "Start Date is required";
    if (!coverage) newErrors.coverage = "Coverage is required";
    if (!dob1) newErrors.dob1 = "Traveller 1 DOB is required";

    if (insured === "2-persons") {
      if (!dob2) newErrors.dob2 = "Traveller 2 DOB is required";
    }

    // --- No. of Days ---
    if (visaType !== "Super Visa") {
      if (!noOfDays || noOfDays < 7 || noOfDays > 365) {
        newErrors.noOfDays = "Number of days must be between 7-365";
      }
    }

    // --- Coverage options ---
    const coverageValues = coverageOptions.map((opt) => opt.value);
    if (!coverageValues.includes(coverage)) {
      newErrors.coverage = "Invalid coverage selected";
    }

    setHeroErrors(newErrors);

    // scroll to first error
    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = Object.keys(newErrors)[0];
      const el = document.getElementById(`hero-${firstErrorField}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return false;
    }

    return true;
  };

  const handleHeroVisitorQuoteSubmit = (event) => {
    event.preventDefault();

    if (!validateHeroForm()) return;

    const applicant = insured === "2-persons" ? "2" : "1";
    const params = new URLSearchParams({
      frmsub: "1",
      visaType,
      applicant,
      coverage: String(coverage),
      startDate: formatISODate(toNoon(startDate)),
      endDate: formatISODate(toNoon(endDate)),
      "age-one": formatISODate(toNoon(dob1)),
      // The hero banner doesn't ask about pre-existing conditions; default to
      // "No" so the /quote/vtc page still has a value to parse.
      mcone: "No",
    });

    if (applicant === "2") {
      params.set("age-two", formatISODate(toNoon(dob2)));
      params.set("mctwo", "No");
    }

    navigate(`/quote/vtc?${params.toString()}`);
  };

  const handleHeroPrevious = () => {
    setActiveHeroSlide((current) => (current === 0 ? 1 : 0));
  };

  const handleHeroNext = () => {
    setActiveHeroSlide((current) => (current === 1 ? 0 : 1));
  };

 useEffect(() => {
  if (isHeroFormActive) return undefined;

  const heroTimer = window.setInterval(() => {
    setActiveHeroSlide((current) => (current === 1 ? 0 : 1));
  }, 8000);

  return () => window.clearInterval(heroTimer);
}, [activeHeroSlide, isHeroFormActive]);

  const pauseHeroForForm = () => setIsHeroFormActive(true);
  const resumeHeroAfterForm = () => setIsHeroFormActive(false);



  const container = "mx-auto w-full max-w-[1380px] px-4 sm:px-6 lg:px-10";

  const homePageSchema = {
  "@context": "https://schema.org",
  "@type": ["InsuranceAgency", "LocalBusiness"],
  "@id": "https://www.einsured.ca/#organization",
  name: "Einsured",
  alternateName: "einsured.ca - Life Insurance | Super Visa Insurance",
  url: "https://www.einsured.ca/",
  logo: "https://www.einsured.ca/logo.png",
  image: "https://www.einsured.ca/logo.png",
  description:
    "Einsured is a leading insurance agency in Brampton, Canada, offering life insurance, super visa insurance, health insurance, travel insurance, and investment solutions for individuals and families across Canada.",
  telephone: "+1-844-440-0128",
  email: "info@einsured.ca",
  address: {
    "@type": "PostalAddress",
    streetAddress: "9300 Goreway Drive, Unit #106",
    addressLocality: "Brampton",
    addressRegion: "ON",
    postalCode: "L6P 4N1",
    addressCountry: "CA",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 43.7579458,
    longitude: -79.6950552,
  },
  areaServed: {
    "@type": "Country",
    name: "Canada",
  },
  serviceArea: {
    "@type": "AdministrativeArea",
    name: "Canada",
  },
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer service",
    telephone: "+1-844-440-0128",
    email: "info@einsured.ca",
    areaServed: "CA",
    availableLanguage: ["English"],
  },
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: "10:00",
    closes: "18:00",
  },
  founder: {
    "@type": "Person",
    name: "Aman Ahuja",
  },
  foundingDate: "2017",
  brand: {
    "@type": "Brand",
    name: "Einsured",
  },
  knowsAbout: [
    "Life Insurance",
    "Super Visa Insurance",
    "Health Insurance",
    "Travel Insurance",
    "Investment Planning",
    "RRSP",
    "TFSA",
    "RESP",
  ],
  makesOffer: [
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Life Insurance" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Super Visa Insurance" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Health Insurance" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Travel Insurance" } },
  ],
  hasOfferCatalog: {
  "@type": "OfferCatalog",
  name: "Insurance Services",
  itemListElement: [
    {
      "@type": "OfferCatalog",
      name: "Life Insurance",
      itemListElement: [
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Term Life Insurance" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Permanent Life Insurance" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Universal Life Insurance" } },
      ],
    },
    {
      "@type": "OfferCatalog",
      name: "Travel Insurance",
      itemListElement: [
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Super Visa Insurance" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Visitor Insurance" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "International Student Insurance" } },
      ],
    },
    {
      "@type": "OfferCatalog",
      name: "Living Benefits",
      itemListElement: [
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Critical Illness Insurance" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Disability Insurance" } },
      ],
    },
  ],
},
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.9",
    reviewCount: "282",
    bestRating: "5",
    worstRating: "1",
  },
  sameAs: [
    "https://www.facebook.com/einsured.ca/",
    "https://www.linkedin.com/company/einsured-ca/",
    "https://www.instagram.com/einsured.ca/",
    "https://www.pinterest.com/einsuredca/",
    "https://www.youtube.com/@einsured1546",
    "https://x.com/EinsuredCa",
    "https://maps.app.goo.gl/uQwSfgJrR1ZrayLR7",
  ],
};
  const [scrollDir, setScrollDir] = useState("up");
  const lastY = useRef(0);
  const rafRef = useRef(null);

  const [flippedStep, setFlippedStep] = useState(null);

  const toggleStepFlip = (idx) => {
    setFlippedStep((cur) => (cur === idx ? null : idx));
  };

  useEffect(() => {
    lastY.current = window.scrollY || 0;

    const onScroll = () => {
      if (rafRef.current) return;

      rafRef.current = requestAnimationFrame(() => {
        const y = window.scrollY || 0;
        const diff = y - lastY.current;

        if (Math.abs(diff) > 6) {
          setScrollDir(diff > 0 ? "down" : "up");
          lastY.current = y;
        }

        rafRef.current = null;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const useInViewOnce = (threshold = 0.22) => {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);

    useEffect(() => {
      const el = ref.current;
      if (!el) return;

      const io = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setInView(true);
            io.disconnect();
          }
        },
        { threshold },
      );

      io.observe(el);
      return () => io.disconnect();
    }, [threshold]);

    return [ref, inView];
  };

  const ease = "cubic-bezier(0.22, 1, 0.36, 1)";

  const ENTRANCE_DUR_MULT = 1.55;
  const ENTRANCE_DIST_MULT = 1.9;

  const dur = (ms) => Math.round(ms * ENTRANCE_DUR_MULT);
  const dist = (px) => Math.round(px * ENTRANCE_DIST_MULT);

  const scaleFromBoost = (scaleFrom) => {
    const delta = 1 - Number(scaleFrom || 1);
    const boosted = 1 - delta * ENTRANCE_DIST_MULT;
    return Math.max(0.94, boosted);
  };

  const makeFadeUp = (inView, delayMs = 0, y = 16, durMs = 700) => {
    const Y = dist(y);
    const D = dur(durMs);

    return {
      opacity: inView ? 1 : 0,
      transform: inView ? "translateY(0px)" : `translateY(${Y}px)`,
      transition: `opacity ${D}ms ${ease} ${delayMs}ms, transform ${D}ms ${ease} ${delayMs}ms`,
      willChange: "transform, opacity",
    };
  };

  const makeFadeScale = (
    inView,
    delayMs = 0,
    scaleFrom = 0.985,
    durMs = 750,
  ) => {
    const D = dur(durMs);
    const S = scaleFromBoost(scaleFrom);
    const Y = dist(10);

    return {
      opacity: inView ? 1 : 0,
      transform: inView
        ? "translateY(0px) scale(1)"
        : `translateY(${Y}px) scale(${S})`,
      transition: `opacity ${D}ms ${ease} ${delayMs}ms, transform ${D}ms ${ease} ${delayMs}ms`,
      willChange: "transform, opacity",
    };
  };

  const makeFadeLeft = (inView, delayMs = 0, x = 18, durMs = 700) => {
    const X = dist(x);
    const D = dur(durMs);

    return {
      opacity: inView ? 1 : 0,
      transform: inView ? "translateX(0px)" : `translateX(-${X}px)`,
      transition: `opacity ${D}ms ${ease} ${delayMs}ms, transform ${D}ms ${ease} ${delayMs}ms`,
      willChange: "transform, opacity",
    };
  };

  const makeFadeRight = (inView, delayMs = 0, x = 18, durMs = 700) => {
    const X = dist(x);
    const D = dur(durMs);

    return {
      opacity: inView ? 1 : 0,
      transform: inView ? "translateX(0px)" : `translateX(${X}px)`,
      transition: `opacity ${D}ms ${ease} ${delayMs}ms, transform ${D}ms ${ease} ${delayMs}ms`,
      willChange: "transform, opacity",
    };
  };

  // The hero is above the fold, so it renders visible immediately; waiting
  // for an IntersectionObserver + fade-in would delay Largest Contentful Paint.
  const heroInView = true;
  const [partnersRefSection, partnersInView] = useInViewOnce(0.22);

  const [statsRef, statsInView] = useInViewOnce(0.2);
  const [withoutWithRef, withoutWithInView] = useInViewOnce(0.18);
  const [stepsRef, stepsInView] = useInViewOnce(0.18);
  const [typesRef, typesInView] = useInViewOnce(0.18);
  const [pay20Ref, pay20InView] = useInViewOnce(0.18);
  const [bundleRef, bundleInView] = useInViewOnce(0.18);
  const [connectRef, connectInView] = useInViewOnce(0.18);

  // ------- Working Partners slider -------
  const partnerLogos = React.useMemo(
    () => [
      { src: partner1, alt: "UV Insurance logo" },
      { src: partner2, alt: "GMS logo" },
      { src: partner3, alt: "Manulife logo" },
      { src: partner4, alt: "IA Financial Group logo" },
      { src: partner5, alt: "Foresters Financial logo" },
      { src: partner6, alt: "TuGo logo" },
      { src: partner7, alt: "RBC logo" },
      { src: partner8, alt: "Empire Life logo" },
      { src: partner9, alt: "Sun Life logo" },
      { src: partner10, alt: "Equitable logo" },
      { src: partner11, alt: "Canada Life logo" },
      { src: partner12, alt: "Assumption Life logo" },
      { src: partner13, alt: "The Edge Benefits logo" },
      { src: partner14, alt: "Travel Insurance Limited logo" },
      { src: partner15, alt: "Allianz Insurance logo" },
      { src: partner16, alt: "Secure Travel logo" },
      { src: partner17, alt: "Destination Travel Group logo" },
      { src: partner18, alt: "Ingle Assurance logo" },
      { src: partner19, alt: "Travelance logo" },
      { src: partner20, alt: "BMO Insurance logo" },
      { src: partner21, alt: "Specialty Life Insurance logo" },
      { src: partner22, alt: "Canada Protection Plan logo" },
    ],
    [],
  );

  const partnersRef = React.useRef(null);
  const [partnersPaused, setPartnersPaused] = React.useState(false);

  const slideNext = React.useCallback(() => {
    const el = partnersRef.current;
    if (!el) return;
    const firstChild = el.firstElementChild;
    if (!firstChild) return;

    const step = firstChild.clientWidth + 24;

    const maxScroll = el.scrollWidth - el.clientWidth;
    const nextLeft = el.scrollLeft + step;

    if (nextLeft >= maxScroll - 2) {
      el.scrollTo({ left: 0, behavior: "smooth" });
    } else {
      el.scrollTo({ left: nextLeft, behavior: "smooth" });
    }
  }, []);

  const slidePrev = React.useCallback(() => {
    const el = partnersRef.current;
    if (!el) return;

    const firstChild = el.firstElementChild;
    if (!firstChild) return;

    const step = firstChild.clientWidth + 24;

    const maxScroll = el.scrollWidth - el.clientWidth;
    let nextLeft = el.scrollLeft - step;

    if (nextLeft <= 0) {
      nextLeft = maxScroll;
    }

    el.scrollTo({ left: nextLeft, behavior: "smooth" });
  }, []);

  React.useEffect(() => {
    if (partnersPaused) return;

    const id = setInterval(() => {
      slideNext();
    }, 3000);

    return () => clearInterval(id);
  }, [slideNext, partnersPaused]);

  const [cwName, setCwName] = useState("");
  const [cwPhone, setCwPhone] = useState("");
  const [cwEmail, setCwEmail] = useState("");
  const [cwMessage, setCwMessage] = useState("");

  const [cwTouched, setCwTouched] = useState({
    name: false,
    phone: false,
    email: false,
    message: false,
  });

  const [cwSubmitState, setCwSubmitState] = useState({
    status: "idle",
    banner: "",
  });

  const isEmailValid = (email) => {
    const v = String(email || "").trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  };

  const isPhoneValid = (phone) => {
    const raw = String(phone || "").trim();
    if (!raw) return false;

    const allowed = /^[0-9+\-\s().]*$/.test(raw);
    if (!allowed) return false;

    // count digits
    const digits = raw.replace(/\D/g, "");

    return digits.length >= 10 && digits.length <= 15;
  };

  const cwErrors = React.useMemo(() => {
    const e = {};

    if (!cwName.trim()) e.name = "Please enter your name.";

    if (!String(cwPhone || "").trim()) {
      e.phone = "Please enter your phone number.";
    } else if (!isPhoneValid(cwPhone)) {
      e.phone = "Please enter a valid phone number.";
    }

    if (!String(cwEmail || "").trim()) {
      e.email = "Please enter your email address.";
    } else if (!isEmailValid(cwEmail)) {
      e.email = "Please enter a valid email address.";
    }

    return e;
  }, [cwName, cwPhone, cwEmail]);

  const hasAnyError = Object.keys(cwErrors).length > 0;

  const handleConnectSubmit = async (e) => {
    e.preventDefault();

    setCwTouched({ name: true, phone: true, email: true, message: true });

    if (hasAnyError) {
      setCwSubmitState({
        status: "error",
        banner: "One or more fields have an error. Please check and try again.",
      });

      const firstInvalid = document.querySelector("[aria-invalid='true']");
      if (firstInvalid && typeof firstInvalid.scrollIntoView === "function") {
        firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
        try {
          firstInvalid.focus({ preventScroll: true });
        } catch {}
      }

      return;
    }

    setCwSubmitState({ status: "submitting", banner: "" });

    try {
      const fd = new FormData();
      fd.append("your_name", String(cwName || "").trim());
      fd.append("your-phone", String(cwPhone || "").trim());
      fd.append("your_email", String(cwEmail || "").trim());
      fd.append("your-message", String(cwMessage || "").trim());

      const res = await fetch(
        `https://backend.einsured.ca/api/addContactLead`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
          },
          body: fd,
        },
      );

      const text = await res.text();
      let json = null;
      try {
        json = JSON.parse(text);
      } catch {
        // ignore
      }

      if (!res.ok) {
        throw new Error(
          json?.message || text || `Request failed (${res.status})`,
        );
      }

      setCwName("");
      setCwPhone("");
      setCwEmail("");
      setCwMessage("");
      setCwTouched({ name: false, phone: false, email: false, message: false });

      setCwSubmitState({
        status: "success",
        banner: "Thank you for your message. It has been sent.",
      });

      setTimeout(() => {
        setCwSubmitState((s) =>
          s.status === "success" ? { status: "idle", banner: "" } : s,
        );
      }, 4500);
    } catch (err) {
      setCwSubmitState({
        status: "error",
        banner:
          err?.message ||
          "One or more fields have an error. Please check and try again.",
      });
    }
  };

  const wpAnim = useMemo(() => {
    return {
      h2: {
        ...makeFadeUp(partnersInView, 0, 16, 720),
        animation: partnersInView
          ? `wpPulse ${dur(420)}ms cubic-bezier(0.22, 1, 0.36, 1)`
          : "none",
      },
      p: makeFadeUp(partnersInView, 90, 12, 720),
      slider: makeFadeScale(partnersInView, 160, 0.987, 780),
      logo: (idx) => ({
        opacity: partnersInView ? 1 : 0,
        transform: partnersInView
          ? "translateY(0px)"
          : `translateY(${dist(10)}px)`,
        transition: `opacity ${dur(520)}ms ${ease} ${
          220 + idx * 55
        }ms, transform ${dur(520)}ms ${ease} ${220 + idx * 55}ms`,
        willChange: "transform, opacity",
      }),
      arrow: (side) => ({
        opacity: partnersInView ? 1 : 0,
        transform: partnersInView
          ? "translateY(-50%) translateX(0px)"
          : `translateY(-50%) translateX(${side === "left" ? `-${dist(8)}px` : `${dist(8)}px`})`,
        transition: `opacity ${dur(620)}ms ${ease} 230ms, transform ${dur(620)}ms ${ease} 230ms`,
        willChange: "transform, opacity",
      }),
    };
  }, [partnersInView, scrollDir]);



  //  STATS counting animation
  const [statCoverage, setStatCoverage] = useState(0); // to 50
  const [statPolicies, setStatPolicies] = useState(0); // to 10000
  const [statClients, setStatClients] = useState(0); // to 1000
  const statsStartedRef = useRef(false);

  useEffect(() => {
    if (!statsInView) return;
    if (statsStartedRef.current) return;
    statsStartedRef.current = true;

    const durCount = 1200;
    const start = performance.now();

    const animate = (now) => {
      const t = Math.min(1, (now - start) / durCount);
      const eased = 1 - Math.pow(1 - t, 3);

      setStatCoverage(Math.round(50 * eased));
      setStatPolicies(Math.round(10000 * eased));
      setStatClients(Math.round(1000 * eased));

      if (t < 1) requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }, [statsInView]);

  const formatNumber = (n) => {
    try {
      return new Intl.NumberFormat("en-CA").format(Number(n || 0));
    } catch {
      return String(n || 0);
    }
  };

  const personalInsuranceCards = [
  {
    id: "term-life",
    icon: Users,
    title: "Term Life Insurance",
    explanation:
      "Get affordable life insurance coverage for a set period, such as 10, 20, or 30 years, to protect your family’s financial future. Your beneficiaries receive a tax-free death benefit if you pass away during the policy term.",
    rateLabel: "Price:",
    rate: "$10.98",
    disclaimer:
      "Displayed prices are based on a 30-year-old, non-smoking male in generally good health, with $100,000 in coverage for a 20-year term. Rates may vary based on individual circumstances. Conditions apply.",
    ctaLink: "https://compulife.org/website/655/quoter.html",
  },
  {
    id: "permanent-life",
    icon: ShieldCheck,
    title: "Permanent Life Insurance",
    explanation:
      "Protect your loved ones with lifelong coverage and build cash value over time. Permanent life insurance can provide lasting financial protection while supporting your long-term financial goals.",
    rateLabel: "Price:",
    rate: "$60.03",
    disclaimer:
      "Displayed prices are based on a 30-year-old, non-smoking male in generally good health, with $100,000 of Whole Life coverage. Rates may vary based on individual circumstances. Conditions apply.",
    ctaLink: "https://compulife.org/website/655/quoter.html",
  },
  {
    id: "critical-illness",
    icon: Activity,
    title: "Critical Illness Insurance",
    explanation:
      "A serious illness can bring unexpected expenses and time away from work. Critical illness insurance provides a lump-sum benefit if you’re diagnosed with a covered condition, helping you focus on recovery, not finances.",
    rateLabel: "Price:",
    rate: "$14.03",
    disclaimer:
      "Displayed prices are based on a 30-year-old, non-smoking male in generally good health, with $25,000 in coverage for 25 illnesses over a 20-year term. Rates may vary based on individual circumstances. Conditions apply.",
    ctaLink: "https://book.einsured.ca/widget/bookings/ei-team",
  },
  {
    id: "disability-insurance",
    icon: Accessibility,
    title: "Disability Insurance",
    explanation:
      "An illness or injury could affect your ability to earn an income. Disability insurance can provide monthly income replacement when you’re unable to work due to a covered disability.",
    rateLabel: "Price:",
    rate: "$31.19",
    disclaimer:
      "Displayed prices are based on a 30-year-old, non-smoking male in generally good health, with a $15/month benefit covering a 2-year benefit period. Rates may vary based on individual circumstances. Conditions apply.",
    ctaLink: "https://book.einsured.ca/widget/bookings/ei-team",
  },
  {
    id: "super-visa-insurance",
    icon: Luggage,
    title: "Super Visa Insurance",
    explanation:
      "Give your visiting parents or grandparents protection against unexpected medical expenses in Canada. Choose from flexible coverage options designed to meet Super Visa requirements and your family’s needs.",
    rateLabel: "Price:",
    rate: "$71.75",
    disclaimer:
      "Displayed prices are based on a 50-year-old, non-smoking male with no pre-existing medical conditions, $100,000 in coverage, and a $1,000 deductible. Rates may vary based on individual circumstances. Conditions apply.",
    ctaLink: "https://www.einsured.ca/quote/vtc",
  },
  {
  id: "no-medical-insurance",
  icon: ShieldCheck,
  title: "No Medical Insurance",
  explanation:
    "Get life insurance coverage without a traditional medical exam or extensive medical questions, depending on the plan. It can be a convenient option for those looking for simpler and more accessible coverage.",
  rateLabel: "Price:",
  rate: "$10.98",
  disclaimer:
  "Displayed prices are based on a 30-year-old, non-smoking male in generally good health, with $100,000 in coverage for a 20-year term. Rates may vary based on individual circumstances. Conditions apply.",
  ctaLink: "https://book.einsured.ca/widget/bookings/ei-team",
},
];

  const ratesDisclaimer =
    "Disclaimer: The rates shown above are for illustrative purposes only and are subject to change. We offer a range of coverage options, and premiums may vary based on age, medical history, coverage amount, term, benefit period, and other individual factors. Actual rates may be higher or lower. Conditions apply.";


  return (
    <div className="w-full overflow-x-hidden">
      <SEO
        title="Trusted Insurance & Investment Brokerage in Canada – einsured.ca"
        description="einsured.ca is a Canadian insurance and investment brokerage offering trusted guidance and tailored solutions to help you plan with confidence."
        keywords="Insurance & Investment Brokerage, life insurance canada, travel insurance, super visa insurance"
      />
      {/* Skip on staging/preview hosts so this LocalBusiness schema (address,
          phone, ratings) never gets attributed to the real business there. */}
      {isProductionHost() ? (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(homePageSchema)}
          </script>
        </Helmet>
      ) : null}
     {/* HERO CAROUSEL */}
<section
  className="relative overflow-hidden bg-[#49628b] text-white"
>
  <style>{`
    @keyframes heroBannerSlideIn {
      from {
        opacity: 0;
        transform: translateX(100%);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }

    .hero-banner-slide-active {
      animation: heroBannerSlideIn 700ms cubic-bezier(0.22, 1, 0.36, 1) both;
    }

    @media (prefers-reduced-motion: reduce) {
      .hero-banner-slide-active {
        animation: none;
      }
    }
  `}</style>
  {/* Existing circular background details */}
  <div
    aria-hidden="true"
    className="pointer-events-none absolute -right-[210px] -top-[300px] h-[780px] w-[780px] rounded-full border border-sky-400/10"
  />
  <div
    aria-hidden="true"
    className="pointer-events-none absolute -right-[120px] -top-[210px] h-[610px] w-[610px] rounded-full border border-sky-400/10"
  />
  <div
    aria-hidden="true"
    className="pointer-events-none absolute -right-[35px] -top-[125px] h-[440px] w-[440px] rounded-full border border-sky-400/10"
  />

  <div className={`${container} relative z-10 overflow-hidden pb-8 pt-16 sm:pb-9 sm:pt-12 lg:pb-10 lg:pt-11`}>
    {/* =========================================================
        BANNER 1 — LIFE INSURANCE
        Existing banner stays the same
    ========================================================= */}
    <div className="grid w-full grid-cols-[minmax(0,1fr)] overflow-hidden">
      <div
        className={`col-start-1 row-start-1 min-w-0 ${
          activeHeroSlide === 0
            ? "visible z-10 hero-banner-slide-active"
            : "hidden pointer-events-none z-0 sm:block sm:invisible"
        }`}
        aria-hidden={activeHeroSlide !== 0}
      >
      <div className="grid h-auto min-w-0 grid-cols-1 items-start gap-5 lg:h-full lg:grid-cols-[minmax(0,1.16fr)_minmax(430px,0.84fr)] lg:gap-12 xl:gap-16">
        {/* LEFT CONTENT */}
        <div className="order-1 mx-auto flex w-full max-w-[720px] flex-col items-center text-center lg:mx-0 lg:items-start lg:text-left">
          <div
            style={makeFadeUp(heroInView, 0, 14, 720)}
            className="mb-5 flex items-center justify-center gap-3 text-[9px] font-semibold uppercase tracking-[0.2em] text-[#E8EEF0] sm:text-[11px] lg:justify-start"
          >
            <span className="h-[2px] w-8 shrink-0 bg-[#20a7df]" />
            <span>Independent Canadian Brokerage · Est. 2017</span>
          </div>

          <h1
            style={makeFadeUp(heroInView, 60, 18, 780)}
            className="font-bold leading-[1.06] tracking-[-0.025em] text-[34px] sm:text-[46px] lg:text-[52px] xl:text-[58px]"
          >
            <span className="block whitespace-nowrap">
              The right coverage.
            </span>

            <span className="block whitespace-nowrap text-[#20a7df]">
              Expertly matched
            </span>

            <span className="block whitespace-nowrap">
              to your life.
            </span>
          </h1>

          <div
            style={makeFadeUp(heroInView, 120, 14, 760)}
            className="mt-5 max-w-[625px] text-[15px] leading-7 text-white/80 sm:text-[17px] sm:leading-8"
          >
            <p>
              We compare 15+ top Canadian insurers and give you clear,
              unbiased advice so you&apos;re protected without overpaying.
            </p>

            <p className="mt-1">
              No jargon. No pressure.
            </p>
          </div>

          <div
            style={makeFadeUp(heroInView, 175, 12, 720)}
            className="mt-5 flex flex-col items-center lg:items-start"
          >
            <p className="text-sm font-semibold text-white sm:text-base">
              Rated 4.9/5 across Canada
            </p>

            <div
              className="mt-2 flex items-center gap-1"
              role="img" aria-label="Rated 4.9 out of 5 across Canada"
            >
              {Array.from({ length: 5 }, (_, i) => (
                <Star
                  key={i}
                  size={20}
                  className="fill-yellow-400 text-yellow-400"
                  aria-hidden="true"
                  style={{
                    opacity: heroInView ? 1 : 0,
                    transform: heroInView
                      ? "translateY(0px)"
                      : `translateY(${dist(8)}px)`,
                    transition: `opacity ${dur(520)}ms ${ease} ${
                      220 + i * 60
                    }ms, transform ${dur(520)}ms ${ease} ${
                      220 + i * 60
                    }ms`,
                    willChange: "transform, opacity",
                  }}
                />
              ))}
            </div>
          </div>

          <div
            style={makeFadeUp(heroInView, 240, 10, 700)}
            className="mt-6 flex w-full justify-center sm:w-auto lg:justify-start"
          >
            <button
              type="button"
              onClick={handleLifeInsuranceCta}
              className="inline-flex min-h-12 items-center justify-center rounded-full bg-[#20a7df] px-7 py-3 text-base font-semibold text-[#0a2540] shadow-[0_12px_30px_rgba(8,183,239,0.28)] transition duration-300 hover:-translate-y-0.5 hover:bg-[#20a7df] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#49628b]"
            >
              Life Insurance Quote
            </button>
          </div>
        </div>

        {/* MOBILE NAVIGATION — consistently placed directly above the card */}
        <div className="order-2 flex w-full items-center justify-between px-1 sm:hidden">
          <button
            type="button"
            onClick={handleHeroPrevious}
            aria-label="Previous banner"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/35 bg-white/10 text-white shadow-sm backdrop-blur-sm transition active:scale-95"
          >
            <ChevronLeft size={22} />
          </button>

          <button
            type="button"
            onClick={handleHeroNext}
            aria-label="Next banner"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/35 bg-white/10 text-white shadow-sm backdrop-blur-sm transition active:scale-95"
          >
            <ChevronRight size={22} />
          </button>
        </div>

        {/* RIGHT CONTENT — EXISTING COMPULIFE CALCULATOR */}
        <div
          className="order-3 w-full lg:order-2"
          style={makeFadeRight(heroInView, 90, 18, 780)}
          onMouseEnter={pauseHeroForForm}
          onMouseLeave={resumeHeroAfterForm}
          onFocusCapture={pauseHeroForForm}
          onBlurCapture={resumeHeroAfterForm}
          onTouchStart={pauseHeroForForm}
        >
          <div className="mx-auto w-full max-w-[500px]">
            <div
              className="
                relative
                h-[480px]
                w-full
                overflow-x-hidden
                overflow-y-auto
                rounded-[18px]
                bg-white
                shadow-[0_20px_50px_rgba(0,0,0,0.32)]
                sm:h-[520px]
                sm:rounded-[20px]
                lg:h-[520px]
              "
            >
              <iframe
                src="https://compulife.org/website/655/quoter.html"
                title="CompuLife Life Insurance Quote Calculator"
                loading="eager"
                referrerPolicy="strict-origin-when-cross-origin"
                className="
                  absolute
                  left-0
                  top-0
                  h-[700px]
                  w-[125%]
                  max-w-none
                  origin-top-left
                  scale-[0.8]
                  border-0
                  bg-white
                  sm:h-[650px]
                  sm:w-[111.12%]
                  sm:scale-90
                  lg:h-[650px]
                  lg:w-full
                  lg:scale-100
                "
              >
                <a href="https://compulife.org/website/655/quoter.html">
                  Open the CompuLife Life Insurance Quote Calculator
                </a>
              </iframe>
            </div>
          </div>
        </div>
      </div>
      </div>

    {/* =========================================================
        BANNER 2 — VISITOR / SUPER VISA
    ========================================================= */}
      <div
        className={`col-start-1 row-start-1 min-w-0 ${
          activeHeroSlide === 1
            ? "visible z-10 hero-banner-slide-active"
            : "hidden pointer-events-none z-0 sm:block sm:invisible"
        }`}
        aria-hidden={activeHeroSlide !== 1}
      >
      <div className="grid h-full min-w-0 grid-cols-1 items-start gap-5 lg:grid-cols-2 lg:gap-12 xl:gap-16">
        {/* LEFT CONTENT */}
        <div className="mx-auto flex w-full max-w-[720px] flex-col items-center text-center lg:mx-0 lg:items-start lg:text-left">
          <h2 className="text-[34px] font-bold leading-[1.1] tracking-[-0.025em] text-white sm:text-[46px] lg:text-[52px]">
            Visitor &amp; <span className="text-[#20a7df]">Super Visa</span>{" "}
            Insurance for Canada
          </h2>

         <div className="mt-6 max-w-[680px] text-[15px] leading-7 text-white/80 sm:text-[17px] sm:leading-8">
  <p>
    Compare Visitor to Canada Insurance plans from 13+ top Canadian
    insurers and find the right coverage for your loved ones.
  </p>

  <p className="mt-1">
    Licensed advisors. Instant policy delivery. Refundable and affordable
    policies.
  </p>

 <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-3 text-sm font-semibold text-white/80 lg:flex-nowrap lg:justify-start lg:gap-x-3">
  <div className="flex items-center gap-1.5">
    <ShieldCheck
      size={18}
      className="shrink-0 text-[#20a7df]"
      aria-hidden="true"
    />
    <span>Licensed advisors</span>
  </div>

  <span
    className="hidden h-3 w-px bg-white/30 sm:block"
    aria-hidden="true"
  />

  <div className="flex items-center gap-1.5">
    <Search
      size={18}
      className="shrink-0 text-[#20a7df]"
      aria-hidden="true"
    />
    <span>Instant policy delivery</span>
  </div>

  <span
    className="hidden h-3 w-px bg-white/30 sm:block"
    aria-hidden="true"
  />

  <div className="flex items-center gap-1.5">
    <Users
      size={18}
      className="shrink-0 text-[#20a7df]"
      aria-hidden="true"
    />
    <span>Refundable and affordable policies</span>
  </div>
</div>
</div>

<div className="mt-5 flex flex-col items-center lg:items-start">
  <p className="text-sm font-semibold text-white sm:text-base">
    Rated 4.9/5 across Canada
  </p>

  <div
    className="mt-2 flex items-center gap-1"
    role="img" aria-label="Rated 4.9 out of 5 across Canada"
  >
    {Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        size={20}
        className="fill-yellow-400 text-yellow-400"
        aria-hidden="true"
        style={{
          opacity: heroInView ? 1 : 0,
          transform: heroInView
            ? "translateY(0px)"
            : `translateY(${dist(8)}px)`,
          transition: `opacity ${dur(520)}ms ${ease} ${
            220 + i * 60
          }ms, transform ${dur(520)}ms ${ease} ${
            220 + i * 60
          }ms`,
          willChange: "transform, opacity",
        }}
      />
    ))}
  </div>
</div>

          <div className="mt-6 flex w-full justify-center sm:w-auto lg:justify-start">
            <button
              type="button"
              onClick={handleVisitorSuperVisaCta}
              className="inline-flex min-h-12 items-center justify-center rounded-full bg-[#20a7df] px-7 py-3 text-base font-semibold text-[#0a2540] shadow-[0_12px_30px_rgba(8,183,239,0.28)] transition duration-300 hover:-translate-y-0.5 hover:bg-[#20a7df] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#49628b]"
            >
              Get Your Quote
            </button>
          </div>
        </div>

        {/* MOBILE NAVIGATION — same position and spacing as Banner 1 */}
        <div className="order-2 flex w-full items-center justify-between px-1 sm:hidden">
          <button
            type="button"
            onClick={handleHeroPrevious}
            aria-label="Previous banner"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/35 bg-white/10 text-white shadow-sm backdrop-blur-sm transition active:scale-95"
          >
            <ChevronLeft size={22} />
          </button>

          <button
            type="button"
            onClick={handleHeroNext}
            aria-label="Next banner"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/35 bg-white/10 text-white shadow-sm backdrop-blur-sm transition active:scale-95"
          >
            <ChevronRight size={22} />
          </button>
        </div>

       {/* RIGHT CONTENT — VISITOR / SUPER VISA QUOTE PREVIEW */}
<div
  className="order-3 w-full lg:order-2"
  style={makeFadeRight(heroInView, 90, 18, 780)}
  onMouseEnter={pauseHeroForForm}
  onMouseLeave={resumeHeroAfterForm}
  onFocusCapture={pauseHeroForForm}
  onBlurCapture={resumeHeroAfterForm}
  onTouchStart={pauseHeroForForm}
>
  <div className="mx-auto w-full max-w-[540px]">
    <form
  onSubmit={handleHeroVisitorQuoteSubmit}
  onChange={pauseHeroForForm}
  noValidate
  className="h-auto overflow-y-auto overscroll-contain rounded-[20px] bg-white p-5 text-left text-[#444E59] shadow-[0_20px_50px_rgba(0,0,0,0.28)] sm:p-6"
>
      <div className="mb-5 text-center">
        <h3 className="text-xl font-bold text-[#49628b] sm:text-2xl">
          Visitor &amp; Super Visa Quote
        </h3>
      </div>

      <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
        {/* SUPER VISA */}
        <div>
          <label className="mb-2 block text-sm font-semibold text-[#444E59]">
            Is this for a Super Visa?
          </label>

          <div className="grid grid-cols-2 overflow-hidden rounded-md border border-gray-300">
            <label className="cursor-pointer">
              <input
                type="radio"
                name="hero_super_visa"
                checked={visaType === "Super Visa"}
                onChange={() => setVisaType("Super Visa")}
                className="peer sr-only"
              />

             <span className="flex h-10 items-center justify-center bg-[#E8EEF0] text-sm font-medium text-[#444E59] peer-checked:bg-[#20a7df] peer-checked:text-[#0a2540]">
  Yes
</span>
            </label>

            <label className="cursor-pointer">
              <input
                type="radio"
                name="hero_super_visa"
                checked={visaType === "Visitor Visa"}
                onChange={() => setVisaType("Visitor Visa")}
                className="peer sr-only"
              />

              <span className="flex h-10 items-center justify-center bg-[#E8EEF0] text-sm font-medium text-[#444E59] peer-checked:bg-[#20a7df] peer-checked:text-[#0a2540]">
                No
              </span>
            </label>
          </div>
        </div>

        {/* INSURED */}
        <div>
          <label className="mb-2 block text-sm font-semibold text-[#444E59]">
            Insured?
          </label>

          <div className="grid grid-cols-2 overflow-hidden rounded-md border border-gray-300">
            <label className="cursor-pointer">
              <input
                type="radio"
                name="hero_insured"
                checked={insured === "single"}
                onChange={() => setInsured("single")}
                className="peer sr-only"
              />

           <span className="flex h-10 items-center justify-center bg-[#E8EEF0] text-sm font-medium text-[#444E59] peer-checked:bg-[#20a7df] peer-checked:text-[#0a2540]">
  Single
</span>
            </label>

            <label className="cursor-pointer">
              <input
                type="radio"
                name="hero_insured"
                checked={insured === "2-persons"}
                onChange={() => setInsured("2-persons")}
                className="peer sr-only"
              />

              <span className="flex h-10 items-center justify-center bg-[#E8EEF0] text-sm font-medium text-[#444E59] peer-checked:bg-[#20a7df] peer-checked:text-[#0a2540]">
                2 Persons
              </span>
            </label>
          </div>
        </div>

        {/* COVERAGE */}
        <div>
          <label
            htmlFor="hero-coverage"
            className="mb-2 block text-sm font-semibold text-[#444E59]"
          >
            Coverage (CAD)
          </label>

          <select
            id="hero-coverage"
            value={coverage}
            onChange={(event) => setCoverage(Number(event.target.value))}
            className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-[#444E59] outline-none transition focus:border-[#20a7df] focus:ring-1 focus:ring-[#20a7df]"
          >
            {coverageOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {heroErrors.coverage && (
            <p className="mt-1 text-xs font-medium text-red-500">{heroErrors.coverage}</p>
          )}
        </div>

        {/* START DATE — same DateField component as /quote/vtc */}
        <div>
          <label
            htmlFor="hero-startDate"
            className="mb-2 block text-sm font-semibold text-[#444E59]"
          >
            Start Date
          </label>

          <HeroDateField
            id="hero-startDate"
            value={startDate}
            onChange={(date) => setStartDate(date)}
            minDate={today}
            maxDate={maxStartDate}
            openId={openHeroDateId}
            setOpenId={setOpenHeroDateId}
          />
          {heroErrors.startDate && (
            <p className="mt-1 text-xs font-medium text-red-500">{heroErrors.startDate}</p>
          )}
        </div>

        {/* NUMBER OF DAYS */}
        <div>
          <label
            htmlFor="hero-noOfDays"
            className="mb-2 block text-sm font-semibold text-[#444E59]"
          >
            No. of Days
          </label>

          <input
            id="hero-noOfDays"
            type="number"
            min={visaType === "Super Visa" ? 365 : 7}
            max={365}
            readOnly={visaType === "Super Visa"}
            value={noOfDays}
            onChange={handleNoOfDaysChange}
            onBlur={handleNoOfDaysBlur}
            className={`h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-[#444E59] outline-none transition focus:border-[#20a7df] focus:ring-1 focus:ring-[#20a7df] ${
              visaType === "Super Visa" ? "cursor-not-allowed" : ""
            }`}
          />
          {heroErrors.noOfDays && (
            <p className="mt-1 text-xs font-medium text-red-500">{heroErrors.noOfDays}</p>
          )}
        </div>

        {/* TRAVELLER 1 DATE OF BIRTH — same DateField component as /quote/vtc */}
        <div>
          <label
            htmlFor="hero-dob1"
            className="mb-2 block text-sm font-semibold text-[#444E59]"
          >
            Date of Birth
          </label>

          <HeroDateField
            id="hero-dob1"
            value={dob1}
            onChange={(date) => setDob1(date)}
            minDate={minDob}
            maxDate={today}
            openId={openHeroDateId}
            setOpenId={setOpenHeroDateId}
          />
          {heroErrors.dob1 && (
            <p className="mt-1 text-xs font-medium text-red-500">{heroErrors.dob1}</p>
          )}
        </div>

        {insured === "2-persons" && (
          /* TRAVELLER 2 DATE OF BIRTH — same DateField component as /quote/vtc */
          <div>
            <label
              htmlFor="hero-dob2"
              className="mb-2 block text-sm font-semibold text-[#444E59]"
            >
              Person 2 Date of Birth
            </label>

            <HeroDateField
              id="hero-dob2"
              value={dob2}
              onChange={(date) => setDob2(date)}
              minDate={minDob}
              maxDate={today}
              openId={openHeroDateId}
              setOpenId={setOpenHeroDateId}
            />
            {heroErrors.dob2 && (
              <p className="mt-1 text-xs font-medium text-red-500">{heroErrors.dob2}</p>
            )}
          </div>
        )}
      </div>

      <button
        type="submit"
        className="mt-5 flex min-h-12 w-full items-center justify-center rounded-md bg-[#49628b] px-6 py-3 text-base font-bold text-white shadow-[0_10px_24px_rgba(73,98,139,0.22)] transition duration-300 hover:bg-[#3e5579] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#20a7df] focus-visible:ring-offset-2"
      >
        Calculate
      </button>

      <p className="mt-3 text-center text-xs text-[#444E59]/90">
        Continue to view available insurance quotes
      </p>
    </form>
  </div>
</div>
      </div>
      </div>
    </div>
  </div>

  {/* CAROUSEL PREVIOUS BUTTON */}
  <button
    type="button"
    onClick={handleHeroPrevious}
    aria-label="Previous banner"
    className="absolute left-3 top-1/2 z-20 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-white/10 text-white backdrop-blur-sm transition hover:bg-white/20 sm:flex"
  >
    <ChevronLeft size={24} />
  </button>

  {/* CAROUSEL NEXT BUTTON */}
  <button
    type="button"
    onClick={handleHeroNext}
    aria-label="Next banner"
    className="absolute right-3 top-1/2 z-20 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-white/10 text-white backdrop-blur-sm transition hover:bg-white/20 sm:flex"
  >
    <ChevronRight size={24} />
  </button>

  {/* MOBILE / DESKTOP SLIDE INDICATORS */}
  <div className="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
    <button
      type="button"
      onClick={() => setActiveHeroSlide(0)}
      aria-label="Show Life Insurance banner"
      className={`h-2.5 rounded-full transition-all duration-300 ${
        activeHeroSlide === 0
          ? "w-8 bg-[#20a7df]"
          : "w-2.5 bg-white/50"
      }`}
    />

    <button
      type="button"
      onClick={() => setActiveHeroSlide(1)}
      aria-label="Show Visitor and Super Visa Insurance banner"
      className={`h-2.5 rounded-full transition-all duration-300 ${
        activeHeroSlide === 1
          ? "w-8 bg-[#20a7df]"
          : "w-2.5 bg-white/50"
      }`}
    />
  </div>
</section>
      {/* WORKING PARTNERS */}
      <section ref={partnersRefSection} className="py-14 md:py-20 bg-[#E8EEF0]">
        <style>{`
          @keyframes wpPulse {
            0%   { transform: translateY(0px) scale(1); }
            50%  { transform: translateY(0px) scale(1.12); }
            100% { transform: translateY(0px) scale(1); }
          }
        `}</style>

        <div className={`${container} text-center`}>
          <h2
            key={`${partnersInView}-${scrollDir}`}
            style={wpAnim.h2}
            className="text-2xl sm:text-3xl md:text-5xl font-bold text-[#49628b] mb-3"
          >
            Working Partners
          </h2>
          <p style={wpAnim.p} className="text-gray-600 max-w-3xl mx-auto">
            einsured.ca deals with the most reputable companies providing
            insurance solutions for over 100+ years
          </p>

          {/* Slider */}
          <div
            style={wpAnim.slider}
            className="relative mt-10"
            onMouseEnter={() => setPartnersPaused(true)}
            onMouseLeave={() => setPartnersPaused(false)}
          >
            {/* Left arrow */}
            <button
              type="button"
              onClick={slidePrev}
              aria-label="Previous partners"
              style={wpAnim.arrow("left")}
              className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 z-10
               w-10 h-10 items-center justify-center rounded-full
               bg-white shadow-md border border-gray-200
               hover:shadow-lg hover:scale-105 transition"
            >
              <ChevronLeft className="w-5 h-5 text-[#49628b]" />
            </button>

            <div className="px-14 sm:px-16 md:px-20 lg:px-24 overflow-hidden">
              <div
                ref={partnersRef}
                className="flex items-center gap-6 sm:gap-10 md:gap-12 lg:gap-16
                 overflow-x-auto sm:overflow-hidden
                 scroll-smooth
                 snap-x snap-mandatory sm:snap-none
                 touch-pan-x
                 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                {partnerLogos.map((logo, idx) => (
                  <div
                    key={idx}
                    style={wpAnim.logo(idx)}
                    className="flex-shrink-0 flex items-center justify-center
     w-32 sm:w-40 md:w-48 lg:w-52 snap-center"
                  >
                    <img
                      src={logo.src}
                      alt={logo.alt}
                      loading="lazy"
                      width={200}
                      height={100}
                      className="w-auto max-h-12 sm:max-h-14 md:max-h-16 object-contain
       transition-transform duration-300 ease-out
       hover:scale-[1.06] hover:-translate-y-[2px]"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Right arrow */}
            <button
              type="button"
              onClick={slideNext}
              aria-label="Next partners"
              style={wpAnim.arrow("right")}
              className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 z-10
               w-10 h-10 items-center justify-center rounded-full
               bg-white shadow-md border border-gray-200
               hover:shadow-lg hover:scale-105 transition"
            >
              <ChevronRight className="w-5 h-5 text-[#49628b]" />
            </button>
          </div>
        </div>
      </section>

      {/* WHY THIS MATTERS */}
      <section className="bg-white py-14 sm:py-16 md:py-20">
        <div className={container}>
          <div className="mx-auto max-w-4xl text-center">
            <div className="flex items-center justify-center gap-3 text-xs font-bold uppercase tracking-[0.2em] text-[#20a7df] sm:text-sm">
              <span className="h-[2px] w-8 bg-[#20a7df]" />
              <span>Why this matters</span>
              <span className="h-[2px] w-8 bg-[#20a7df]" />
            </div>

            <h2 className="mt-4 text-3xl font-bold leading-tight tracking-[-0.02em] text-[#49628b] sm:text-4xl md:text-5xl">
              The risk isn&apos;t hypothetical — it&apos;s just spread unevenly
              across a lifetime.
            </h2>

            <p className="mx-auto mt-5 max-w-3xl text-base leading-7 text-[#444E59] sm:text-lg sm:leading-8">
              Most people start shopping for coverage right after something
              happens to someone they know. The numbers say it&apos;s worth looking
              sooner.
            </p>
          </div>

          <div className="mx-auto mt-10 grid max-w-[1280px] grid-cols-1 gap-5 text-center sm:mt-12 sm:grid-cols-2 sm:text-left lg:grid-cols-4 lg:gap-6">
            <article className="flex h-full flex-col rounded-[18px] border border-[#49628b]/10 bg-[#E8EEF0] p-6 shadow-[0_12px_30px_rgba(73,98,139,0.10)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_18px_38px_rgba(73,98,139,0.16)] sm:p-7">
              <p className="text-4xl font-bold tracking-tight text-[#20a7df] sm:text-5xl">
                1 in 3
              </p>
              <p className="mt-5 flex-1 text-[15px] leading-7 text-[#444E59] sm:text-base">
  Working-age Canadians will become disabled and unable to work
  before age 65, a more likely outcome than dying during those
  same years.
</p>
              <p className="mt-6 text-xs font-bold uppercase tracking-[0.14em] text-[#49628b]">
                RBC Insurance
              </p>
            </article>

            <article className="flex h-full flex-col rounded-[18px] border border-[#49628b]/10 bg-[#E8EEF0] p-6 shadow-[0_12px_30px_rgba(73,98,139,0.10)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_18px_38px_rgba(73,98,139,0.16)] sm:p-7">
              <p className="text-4xl font-bold tracking-tight text-[#20a7df] sm:text-5xl">
                2 in 5
              </p>
              <p className="mt-5 flex-1 text-[15px] leading-7 text-[#444E59] sm:text-base">
                Canadians are expected to be diagnosed with cancer at some
                point in their life, and roughly 1 in 4 will die of the
                disease.
              </p>
              <p className="mt-6 text-xs font-bold uppercase tracking-[0.14em] text-[#49628b]">
                Canadian Cancer Society
              </p>
            </article>

            <article className="flex h-full flex-col rounded-[18px] border border-[#49628b]/10 bg-[#E8EEF0] p-6 shadow-[0_12px_30px_rgba(73,98,139,0.10)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_18px_38px_rgba(73,98,139,0.16)] sm:p-7">
              <p className="text-4xl font-bold tracking-tight text-[#20a7df] sm:text-5xl">
                ~10%
              </p>
              <p className="mt-5 flex-1 text-[15px] leading-7 text-[#444E59] sm:text-base">
                Of Canadians carry disability insurance, even though close to
                3 in 10 say their savings would run out within six months of a
                major health setback.
              </p>
              <p className="mt-6 text-xs font-bold uppercase tracking-[0.14em] text-[#49628b]">
                RBC Insurance
              </p>
            </article>

            <article className="flex h-full flex-col rounded-[18px] border border-[#49628b]/10 bg-[#E8EEF0] p-6 shadow-[0_12px_30px_rgba(73,98,139,0.10)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_18px_38px_rgba(73,98,139,0.16)] sm:p-7">
              <p className="text-4xl font-bold tracking-tight text-[#20a7df] sm:text-5xl">
                8.4M
              </p>
              <p className="mt-5 flex-1 text-[15px] leading-7 text-[#444E59] sm:text-base">
  Canadian adults, about 31% of the population, say they need life
  insurance, or need more of it than they currently carry.
</p>
              <p className="mt-6 text-xs font-bold uppercase tracking-[0.14em] text-[#49628b]">
                LIMRA &amp; Life Happens, 2023 Barometer Study
              </p>
            </article>
          </div>

          <div className="mx-auto mt-8 max-w-[1280px] rounded-[18px] border-l-4 border-[#20a7df] bg-[#49628b]/[0.06] px-5 py-5 text-center sm:mt-10 sm:px-7 sm:py-6 sm:text-left">
            <p className="text-[15px] leading-7 text-[#444E59] sm:text-base sm:leading-8">
              Group benefits at work are a start, not a full plan — coverage is
              usually capped at one or two times salary and typically ends the
              day you leave the job. Critical illness, disability, and life
              coverage you own yourself follow you regardless.
            </p>
          </div>
        </div>
      </section>

      {/* STATS SECTION */}
      <section
        ref={statsRef}
        className="py-16 md:py-30 bg-gradient-to-b from-[#20a7df] to-[#49628b] text-white mt-4"
      >
        <div className={container}>
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12 items-start">
              {/* Stat 1 */}
              <div
                style={makeFadeUp(statsInView, 0, 16, 780)}
                className="text-center"
              >
                <div className="text-5xl sm:text-6xl md:text-7xl font-bold tracking-tight drop-shadow">
                  {statCoverage}+ million
                </div>
                <div className="mt-3 text-lg sm:text-xl font-medium opacity-95 drop-shadow">
                  Total Coverage Sold
                </div>
              </div>

              {/* Stat 2 */}
              <div
                style={makeFadeUp(statsInView, 120, 16, 780)}
                className="text-center"
              >
                <div className="text-5xl sm:text-6xl md:text-7xl font-bold tracking-tight drop-shadow">
                  {formatNumber(statPolicies)}+
                </div>
                <div className="mt-3 text-lg sm:text-xl font-medium opacity-95 drop-shadow">
                  Policies Sold
                </div>
              </div>

              {/* Stat 3 */}
              <div
                style={makeFadeUp(statsInView, 220, 16, 780)}
                className="text-center"
              >
                <div className="text-5xl sm:text-6xl md:text-7xl font-bold tracking-tight drop-shadow">
                  {formatNumber(statClients)}+
                </div>
                <div className="mt-3 text-lg sm:text-xl font-medium opacity-95 drop-shadow">
                  Clients Served
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              opacity: statsInView ? 1 : 0,
              transform: statsInView ? "scaleX(1)" : "scaleX(0.92)",
              transformOrigin: "center",
              transition: `opacity ${dur(700)}ms ${ease} 260ms, transform ${dur(700)}ms ${ease} 260ms`,
            }}
          />
        </div>
      </section>

      {/* WITHOUT US vs WITH US */}
      <section ref={withoutWithRef} className="py-16 md:py-20 bg-white">
        <div className={container}>
          <div className="flex flex-col-reverse lg:grid lg:grid-cols-2 gap-8 lg:gap-10 items-start">
            {/* LEFT: Without Us  */}
            <div className="relative w-full">
              {/* Top tab */}
              <div
                className="relative z-10 rounded-[36px] bg-white shadow-xl border border-gray-200
                        px-8 py-6 text-center"
                style={makeFadeLeft(withoutWithInView, 40, 28, 1100)}
              >
                <h2 className="text-4xl sm:text-5xl font-bold text-[#49628b] tracking-tight">
                  Without Us
                </h2>
              </div>

              <div className="mt-6 space-y-6">
                {[
                  "Overpaying Premiums",
                  "Limited Choices",
                  "Missed Savings",
                  "Unclear Coverage",
                  "Limited Support",
                ].map((item, idx) => (
                  <div
                    key={idx}
                    style={makeFadeLeft(
                      withoutWithInView,
                      220 + idx * 140,
                      26,
                      1150,
                    )}
                    className="bg-white shadow-lg border border-gray-200 rounded-md
                         px-6 py-5 flex items-center gap-3"
                  >
                    <Circle className="text-red-500 flex-shrink-0" size={22} />
                    <p className="text-lg sm:text-xl font-medium text-gray-900">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT: With Us  */}
            <div className="relative w-full">
              {/* Top tab */}
              <div
                className="relative z-10 rounded-[36px] bg-sky-500 shadow-xl
                        px-8 py-6 text-center"
                style={makeFadeRight(withoutWithInView, 40, 28, 1100)}
              >
                <h2 className="text-4xl sm:text-5xl font-bold text-white tracking-tight">
                  With Us
                </h2>
              </div>

              <div className="mt-6 space-y-6">
                {[
                  "Affordable Premiums",
                  "Modern Solutions & Multiple Plans",
                  "Savings with Investment Plans",
                  "Full Digital & Streamlined Process",
                  "Dedicated Customer Support",
                ].map((item, idx) => (
                  <div
                    key={idx}
                    style={makeFadeRight(
                      withoutWithInView,
                      220 + idx * 140,
                      26,
                      1150,
                    )}
                    className="shadow-xl rounded-md px-6 py-5 flex items-center gap-3
                         bg-gradient-to-b from-[#20a7df] to-[#49628b]"
                  >
                    <CheckCircle
                      className="text-green-400 mt-1 flex-shrink-0"
                      size={22}
                    />
                    <p className="text-lg sm:text-xl font-semibold text-white">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GET COVERED IN 3 EASY STEPS + CALENDAR */}
      <section ref={stepsRef} className="bg-white">
        <style>{`
          @keyframes stepsPulse {
            0%   { transform: translateY(0px) scale(1); }
            50%  { transform: translateY(0px) scale(1.08); }
            100% { transform: translateY(0px) scale(1); }
          }
        `}</style>

        <div
          className="bg-[#20a7df] py-10 md:py-12"
          style={makeFadeScale(stepsInView, 0, 0.992, 780)}
        >
          <div className={`${container} text-center`}>
            <h2
              key={`${stepsInView}-${scrollDir}`}
              style={{
                ...makeFadeUp(stepsInView, 0, 16, 720),
                animation: stepsInView
                  ? `stepsPulse ${dur(420)}ms cubic-bezier(0.22, 1, 0.36, 1)`
                  : "none",
              }}
              className="text-4xl sm:text-5xl md:text-6xl font-bold text-white drop-shadow"
            >
              Get Covered in 3 Easy Steps
            </h2>
          </div>
        </div>


        {/* Content */}
<div className={`${container} py-8 md:py-10`}>
          <div className="grid grid-cols-1 lg:grid-cols-[1.08fr_0.92fr] gap-8 lg:gap-10 items-start lg:items-stretch">
            <div className="space-y-10 lg:space-y-10 lg:flex lg:flex-col lg:justify-center lg:h-full">
              {/* Steps */}
              <div className="space-y-5">
                {/* STEP 1 */}
                <div
                  style={makeFadeLeft(stepsInView, 120, 18, 720)}
                  className="group [perspective:1200px] cursor-pointer select-none touch-manipulation"
                  role="button"
                  tabIndex={0}
                  aria-pressed={flippedStep === 1}
                  onClick={() => toggleStepFlip(1)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleStepFlip(1);
                    }
                  }}
                >
                  <div className="relative min-h-[110px] sm:h-[110px] lg:h-[116px]">
                    <div
                      className="absolute inset-0 transition-transform duration-1000 ease-in-out
                           [transform-style:preserve-3d]
                           group-hover:[transform:rotateY(180deg)]"
                      style={{
                        transform:
                          flippedStep === 1 ? "rotateY(180deg)" : undefined,
                      }}
                    >
                      {/* FRONT */}
                      <div className="absolute inset-0 flex items-center gap-0 [backface-visibility:hidden]">
                        <div
                          className="w-[150px] sm:w-[175px] lg:w-[185px] h-full flex items-center justify-center
                                  rounded-l-full bg-white shadow-[0_10px_24px_rgba(0,0,0,0.18)]
                                  border border-gray-200"
                        >
                          <span className="text-base sm:text-lg font-medium tracking-wide text-black">
                            STEP 1
                          </span>
                        </div>

                        <div
                          className="flex-1 h-full flex items-center justify-center gap-3
                                  bg-gradient-to-b from-[#20a7df] to-[#49628b]
                                  rounded-r-md shadow-[0_12px_30px_rgba(0,0,0,0.22)]"
                        >
                          <MessageCircle className="text-white" size={22} />
                          <span className="text-lg sm:text-xl font-semibold text-white">
                            Connect With Us
                          </span>
                        </div>
                      </div>

                      {/* BACK */}
                      <div
                        className="absolute inset-0 flex items-center [transform:rotateY(180deg)]
                             [backface-visibility:hidden]"
                      >
                        <div
                          className="w-[150px] sm:w-[175px] lg:w-[185px] h-full flex items-center justify-center
                                  rounded-l-full bg-white shadow-[0_10px_24px_rgba(0,0,0,0.18)]
                                  border border-gray-200"
                        >
                          <span className="text-base sm:text-lg font-medium tracking-wide text-black">
                            STEP 1
                          </span>
                        </div>

                        <div
                          className="flex-1 h-full flex items-center justify-center
                                  bg-white rounded-r-md border border-gray-200
                                  shadow-[0_12px_30px_rgba(0,0,0,0.18)] px-6"
                        >
                          <p className="text-base sm:text-xl font-medium text-[#2a3a57] text-center">
                            Contact us or book a meeting — we&apos;ll guide you
                            personally
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* STEP 2 */}
                <div
                  style={makeFadeLeft(stepsInView, 210, 18, 720)}
                  className="group [perspective:1200px] cursor-pointer select-none touch-manipulation"
                  role="button"
                  tabIndex={0}
                  aria-pressed={flippedStep === 2}
                  onClick={() => toggleStepFlip(2)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleStepFlip(2);
                    }
                  }}
                >
                  <div className="relative min-h-[110px] sm:h-[110px] lg:h-[116px]">
                    <div
                      className="absolute inset-0 transition-transform duration-1000 ease-in-out
                           [transform-style:preserve-3d]
                           group-hover:[transform:rotateY(180deg)]"
                      style={{
                        transform:
                          flippedStep === 2 ? "rotateY(180deg)" : undefined,
                      }}
                    >
                      {/* FRONT */}
                      <div className="absolute inset-0 flex items-center [backface-visibility:hidden]">
                        <div
                          className="w-[150px] sm:w-[175px] lg:w-[185px] h-full flex items-center justify-center
                                  rounded-l-full bg-white shadow-[0_10px_24px_rgba(0,0,0,0.18)]
                                  border border-gray-200"
                        >
                          <span className="text-base sm:text-lg font-medium tracking-wide text-black">
                            STEP 2
                          </span>
                        </div>

                        <div
                          className="flex-1 h-full flex items-center justify-center gap-3
                                  bg-gradient-to-b from-[#20a7df] to-[#49628b]
                                  rounded-r-md shadow-[0_12px_30px_rgba(0,0,0,0.22)]"
                        >
                          <BadgeCheck className="text-white" size={22} />
                          <span className="text-lg sm:text-xl font-semibold text-white">
                            Choose Your Plan
                          </span>
                        </div>
                      </div>

                      {/* BACK */}
                      <div
                        className="absolute inset-0 flex items-center [transform:rotateY(180deg)]
                             [backface-visibility:hidden]"
                      >
                        <div
                          className="w-[150px] sm:w-[175px] lg:w-[185px] h-full flex items-center justify-center
                                  rounded-l-full bg-white shadow-[0_10px_24px_rgba(0,0,0,0.18)]
                                  border border-gray-200"
                        >
                          <span className="text-base sm:text-lg font-medium tracking-wide text-black">
                            STEP 2
                          </span>
                        </div>

                        <div
                          className="flex-1 h-full flex items-center justify-center
                                  bg-white rounded-r-md border border-gray-200
                                  shadow-[0_12px_30px_rgba(0,0,0,0.18)] px-6"
                        >
                          <p className="text-base sm:text-xl font-medium text-[#2a3a57] text-center">
                            We present tailored solutions and you pick what fits
                            best
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* STEP 3 */}
                <div
                  style={makeFadeLeft(stepsInView, 300, 18, 720)}
                  className="group [perspective:1200px] cursor-pointer select-none touch-manipulation"
                  role="button"
                  tabIndex={0}
                  aria-pressed={flippedStep === 3}
                  onClick={() => toggleStepFlip(3)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleStepFlip(3);
                    }
                  }}
                >
                  <div className="relative min-h-[110px] sm:h-[110px] lg:h-[116px]">
                    <div
                      className="absolute inset-0 transition-transform duration-1000 ease-in-out
                           [transform-style:preserve-3d]
                           group-hover:[transform:rotateY(180deg)]"
                      style={{
                        transform:
                          flippedStep === 3 ? "rotateY(180deg)" : undefined,
                      }}
                    >
                      {/* FRONT */}
                      <div className="absolute inset-0 flex items-center [backface-visibility:hidden]">
                        <div
                          className="w-[150px] sm:w-[175px] lg:w-[185px] h-full flex items-center justify-center
                                  rounded-l-full bg-white shadow-[0_10px_24px_rgba(0,0,0,0.18)]
                                  border border-gray-200"
                        >
                          <span className="text-base sm:text-lg font-medium tracking-wide text-black">
                            STEP 3
                          </span>
                        </div>

                        <div
                          className="flex-1 h-full flex items-center justify-center gap-3
                                  bg-gradient-to-b from-[#20a7df] to-[#49628b]
                                  rounded-r-md shadow-[0_12px_30px_rgba(0,0,0,0.22)]"
                        >
                          <PlusSquare className="text-white" size={22} />
                          <span className="text-lg sm:text-xl font-semibold text-white">
                            Get Approved
                          </span>
                        </div>
                      </div>

                      {/* BACK */}
                      <div
                        className="absolute inset-0 flex items-center [transform:rotateY(180deg)]
                             [backface-visibility:hidden]"
                      >
                        <div
                          className="w-[150px] sm:w-[175px] lg:w-[185px] h-full flex items-center justify-center
                                  rounded-l-full bg-white shadow-[0_10px_24px_rgba(0,0,0,0.18)]
                                  border border-gray-200"
                        >
                          <span className="text-base sm:text-lg font-medium tracking-wide text-black">
                            STEP 3
                          </span>
                        </div>

                        <div
                          className="flex-1 h-full flex items-center justify-center
                                  bg-white rounded-r-md border border-gray-200
                                  shadow-[0_12px_30px_rgba(0,0,0,0.18)] px-6"
                        >
                          <p className="text-base sm:text-xl font-medium text-[#2a3a57] text-center">
                            We file your application, and your coverage is
                            activated quickly
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>


            </div>
{/* RIGHT: Calendar */}
<div
  style={makeFadeRight(stepsInView, 180, 18, 760)}
  className="bg-white rounded-[32px] shadow-[0_18px_40px_rgba(0,0,0,0.18)] w-full p-3 sm:p-4"
>
  <div className="rounded-[24px] overflow-hidden border border-gray-200 bg-white">
    <div className="relative h-[560px] sm:h-[600px] md:h-[620px] lg:h-[640px] overflow-y-auto">
      <iframe
        src="https://book.einsured.ca/widget/bookings/ei-team?utm_source=homepage&utm_medium=calendar&utm_campaign=website"
        title="einsured.ca 10 minute review"
        loading="lazy"
        className="w-full border-0 h-[980px]"
      />
    </div>
  </div>
</div>
          </div>
        </div>
      </section>

      {/* PERSONAL INSURANCE */}
      <section ref={typesRef} className="bg-[#E8EEF0] py-16 md:py-24">
        <div className={container}>
          {/* Personal insurance heading */}
          <div
            style={makeFadeUp(typesInView, 0, 16, 760)}
            className="mx-auto max-w-[760px] text-center"
          >
            <div className="flex items-center justify-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[#20a7df] sm:text-xs">
              <span className="h-px w-7 bg-[#20a7df]" />
              <span>What we cover</span>
              <span className="h-px w-7 bg-[#20a7df]" />
            </div>

            <h2 className="mt-4 text-3xl font-extrabold leading-tight tracking-[-0.025em] text-[#49628b] sm:text-4xl md:text-5xl">
              Personal insurance for
              <br className="hidden sm:block" /> every stage of life
            </h2>

            <p className="mx-auto mt-4 max-w-[620px] text-sm leading-6 text-[#444E59] sm:text-base sm:leading-7">
              From your first mortgage to retirement planning, we match the
              right type of coverage to where you are right now.
            </p>
          </div>

          {/* 6 insurance cards: 3 in the first row and 3 in the second row */}
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:mt-14 lg:grid-cols-3 lg:gap-7">
            {personalInsuranceCards.map((card, index) => (
              <InsuranceCard
                key={card.id}
                card={card}
                animationStyle={{
                  opacity: typesInView ? 1 : 0,
                  transform: typesInView
                    ? "translateY(0px)"
                    : `translateY(${dist(14)}px)`,
                  transition: `opacity ${dur(620)}ms ${ease} ${
                    120 + index * 85
                  }ms, transform ${dur(620)}ms ${ease} ${
                    120 + index * 85
                  }ms`,
                  willChange: "transform, opacity",
                }}
              />
            ))}
          </div>

          <p
            style={makeFadeUp(typesInView, 720, 10, 680)}
            className="mx-auto mt-10 max-w-[980px] text-center text-sm italic leading-6 text-[#444E59] sm:text-base"
          >
            {ratesDisclaimer}
          </p>
        </div>
      </section>

      {/* PAY 20 YEARS WHOLE LIFE POLICY SECTION */}
      <section ref={pay20Ref} className=" py-10 md:py-14">
        <style>{`
          @keyframes pay20Pulse {
            0%   { transform: translateY(0px) scale(1); }
            50%  { transform: translateY(0px) scale(1.08); }
            100% { transform: translateY(0px) scale(1); }
          }
        `}</style>

        <div className={container}>
          <div
            style={makeFadeScale(pay20InView, 0, 0.988, 780)}
            className="w-full max-w-[1120px] mx-auto
                 grid grid-cols-1 lg:grid-cols-2
                 rounded-[14px] overflow-hidden
                 shadow-[0_14px_34px_rgba(0,0,0,0.16)]"
          >
            {/* LEFT */}
            <div className="bg-gradient-to-r from-[#20a7df] to-[#49628b] text-white px-7 sm:px-9 md:px-10 py-10 md:py-10">
              <h2
                key={`${pay20InView}-${scrollDir}`}
                style={{
                  ...makeFadeUp(pay20InView, 90, 10, 720),
                  animation: pay20InView
                    ? `pay20Pulse ${dur(420)}ms cubic-bezier(0.22, 1, 0.36, 1)`
                    : "none",
                }}
                className="text-4xl sm:text-5xl md:text-4xl font-bold leading-tight drop-shadow"
              >
                Pay 20 Years Whole Life <br /> Policy
              </h2>

              <p
                style={makeFadeUp(pay20InView, 150, 10, 720)}
                className="mt-5 max-w-[520px] text-base sm:text-lg md:text-xl leading-relaxed text-white/95"
              >
                After 20 years, your policy is fully paid-up, providing
                financial security, guaranteed protection, and peace of mind for
                the rest of your life.
              </p>

              {/* Highlight box */}
              <div
                style={makeFadeUp(pay20InView, 210, 10, 720)}
                className="mt-8 max-w-[560px] bg-[#2f86bb]/35 border border-white/20 rounded-[10px]
                     shadow-[0_10px_22px_rgba(0,0,0,0.18)]
                     px-6 sm:px-7 py-5"
              >
                <p className="text-base sm:text-lg md:text-xl leading-relaxed text-white/95">
                  Over 5,600 clients have chosen this policy, paying premiums
                  for just 20 years to enjoy lifelong life insurance coverage
                </p>
              </div>

              {/* Button */}
              <div
                style={makeFadeUp(pay20InView, 270, 10, 720)}
                className="mt-9 flex justify-center lg:justify-start"
              >
                <button
                  type="button"
                  className="w-full sm:w-[320px]
         py-3.5 rounded-full
         border-2 border-sky-300/70
         text-white font-semibold text-lg
         bg-white/10
         shadow-[0_12px_26px_rgba(0,0,0,0.20)]
         transition duration-300
         hover:bg-white/14 hover:border-sky-200/90"
                  style={{
                    willChange: "transform",
                    transition: `transform ${dur(420)}ms ${ease}, background-color 300ms, border-color 300ms`,
                  }}
                  onClick={() =>
                    window.open(
                      "https://book.einsured.ca/widget/bookings/ei-team?utm_source=homepage&utm_medium=request-quote&utm_campaign=website",
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.transform = "translateY(-2px)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.transform = "translateY(0px)")
                  }
                >
                  Request Quote
                </button>
              </div>
            </div>

            {/* RIGHT */}
            <div
              style={makeFadeRight(pay20InView, 120, 16, 720)}
              className="relative min-h-[280px] sm:min-h-[320px] lg:min-h-full"
            >
              <img
                src={img86}
                alt="Family reviewing a policy"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
                style={{
                  transform: pay20InView ? "scale(1)" : "scale(1.02)",
                  transition: `transform ${dur(900)}ms ${ease}`,
                  willChange: "transform",
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <div
        style={makeFadeUp(
          bundleInView || connectInView || pay20InView,
          0,
          10,
          720,
        )}
      >
        <Testimonials limit={12} autoPlay={true} intervalMs={4500} />
      </div>

      <ScrollToTopButton showAfterPx={420} />

      {/* BUNDLE TERM + WHOLE LIFE SECTION */}
      <section ref={bundleRef} className="bg-[#E8EEF0] py-12 md:py-16">
        <style>{`
          @keyframes bundlePulse {
            0%   { transform: translateY(0px) scale(1); }
            50%  { transform: translateY(0px) scale(1.08); }
            100% { transform: translateY(0px) scale(1); }
          }
        `}</style>

        <div className={container}>
          <div
            style={makeFadeScale(bundleInView, 0, 0.988, 800)}
            className="w-full max-w-[1180px] mx-auto
                 grid grid-cols-1 lg:grid-cols-2
                 rounded-[18px] overflow-hidden
                 shadow-[0_18px_45px_rgba(0,0,0,0.18)]"
          >
            {/* LEFT */}
            <div
              style={makeFadeLeft(bundleInView, 90, 16, 740)}
              className="relative min-h-[320px] sm:min-h-[420px] lg:min-h-full"
            >
              <img
                src={img87}
                alt="Family reviewing insurance options"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
                style={{
                  transform: bundleInView ? "scale(1)" : "scale(1.02)",
                  transition: `transform ${dur(900)}ms ${ease}`,
                  willChange: "transform",
                }}
              />
            </div>

            {/* RIGHT */}
            <div className="bg-gradient-to-r from-[#20a7df] to-[#49628b] text-white px-8 sm:px-10 md:px-12 py-10 md:py-12">
              <h2
                key={`${bundleInView}-${scrollDir}`}
                style={{
                  ...makeFadeUp(bundleInView, 120, 10, 720),
                  animation: bundleInView
                    ? `bundlePulse ${dur(420)}ms cubic-bezier(0.22, 1, 0.36, 1)`
                    : "none",
                }}
                className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight drop-shadow"
              >
                Bundle Term + Whole <br /> Life
              </h2>

              <p
                style={makeFadeUp(bundleInView, 190, 10, 720)}
                className="mt-5 max-w-[560px] text-base sm:text-lg md:text-xl leading-relaxed text-white/95"
              >
                Term Life offers high coverage at a low cost during your most
                financially demanding years, while Whole Life ensures lifelong
                security and builds cash value over time. Together, they create
                a smart balance between affordability and lasting peace of mind
                for you and your family.
              </p>

              {/* Highlight box */}
              <div
                style={makeFadeUp(bundleInView, 250, 10, 720)}
                className="mt-8 max-w-[620px]
                     bg-[#2f86bb]/35 border border-white/20 rounded-[10px]
                     shadow-[0_12px_30px_rgba(0,0,0,0.20)]
                     px-6 sm:px-7 py-6"
              >
                <p className="text-base sm:text-lg md:text-xl leading-relaxed text-white/95">
                  Most of our clients choose this option for enhanced
                  flexibility and affordability.
                </p>
              </div>

              {/* Our Clients + Progress */}
              <div
                style={makeFadeUp(bundleInView, 310, 10, 720)}
                className="mt-8"
              >
                <div className="text-lg font-semibold text-white/95">
                  Our Clients
                </div>

                <div className="mt-3 w-full max-w-[760px]">
                  <div className="w-full bg-white/90 rounded-[6px] overflow-hidden shadow-[0_10px_24px_rgba(0,0,0,0.18)] border border-white/30">
                    <div
                      className="bg-[#20a7df] h-[34px] sm:h-[32px] px-6 flex items-center justify-between"
                      style={{
                        width: bundleInView ? "90%" : "0%",
                        transition: `width ${dur(900)}ms ${ease} 320ms`,
                        willChange: "width",
                      }}
                    >
                      <span className="text-white font-semibold text-sm sm:text-[15px]">
                        Approved
                      </span>

                      <span className="text-white font-semibold text-sm sm:text-[15px]">
                        90%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Button */}
              <div
                style={makeFadeUp(bundleInView, 380, 10, 720)}
                className="mt-10 flex justify-center lg:justify-center"
              >
                <button
                  type="button"
                  className="w-full sm:w-[320px]
       py-4 rounded-full
       border-2 border-sky-300/70
       text-white font-semibold text-lg
       bg-white/10
       shadow-[0_14px_35px_rgba(0,0,0,0.22)]
       transition duration-[350ms]
       hover:bg-white/14 hover:border-sky-200/90"
                  style={{
                    willChange: "transform",
                    transition: `transform ${dur(420)}ms ${ease}, background-color 350ms, border-color 350ms`,
                  }}
                  onClick={() =>
                    window.open(
                      "https://book.einsured.ca/widget/bookings/ei-team?utm_source=homepage&utm_medium=get-approval&utm_campaign=website",
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.transform = "translateY(-2px)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.transform = "translateY(0px)")
                  }
                >
                  Get approval
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONNECT WITH US SECTION */}
      <section ref={connectRef} className="bg-white pt-14 md:pt-0 pb-[50px]">
        <style>{`
          @keyframes connectPulse {
            0%   { transform: translateY(0px) scale(1); }
            50%  { transform: translateY(0px) scale(1.08); }
            100% { transform: translateY(0px) scale(1); }
          }
        `}</style>

        <div
          className="bg-gradient-to-b from-[#20a7df] to-[#49628b] py-12 md:py-14"
          style={makeFadeScale(connectInView, 0, 0.992, 800)}
        >
          <div className={`${container} text-center`}>
            <h2
              key={`${connectInView}-${scrollDir}`}
              style={{
                ...makeFadeUp(connectInView, 0, 16, 720),
                animation: connectInView
                  ? `connectPulse ${dur(420)}ms cubic-bezier(0.22, 1, 0.36, 1)`
                  : "none",
              }}
              className="text-4xl sm:text-5xl md:text-6xl font-bold text-white drop-shadow"
            >
              Connect With Us
            </h2>
          </div>
        </div>

        <div className={`${container} pt-12 md:pt-16 pb-0`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">
            {/* LEFT: FORM */}
            <div
              style={makeFadeLeft(connectInView, 120, 18, 760)}
              className="max-w-[520px] mx-auto lg:mx-0 w-full"
            >
              <form
                onSubmit={handleConnectSubmit}
                noValidate
                className="space-y-7"
              >
                {/* Name */}
                <div style={makeFadeUp(connectInView, 160, 10, 720)}>
                  <label htmlFor="cw_name" className="block text-lg font-semibold text-black mb-2">
                    Your Name <span className="text-red-600">*</span>
                  </label>
                  <input
                    id="cw_name"
                    type="text"
                    name="cw_name"
                    autoComplete="name"
                    maxLength={32}
                    value={cwName}
                    onChange={(e) => {
                      setCwName(e.target.value);
                      if (cwSubmitState.status !== "idle") {
                        setCwSubmitState({ status: "idle", banner: "" });
                      }
                    }}
                    onBlur={() => setCwTouched((t) => ({ ...t, name: true }))}
                    placeholder="Your Name"
                    aria-invalid={cwTouched.name && !!cwErrors.name}
                    aria-describedby="cw_name_error"
                    className={
                      "w-full h-[52px] px-5 rounded-[4px] border text-lg text-gray-700 placeholder:text-gray-400 " +
                      "focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-sky-300 " +
                      (cwTouched.name && cwErrors.name
                        ? "border-red-500 focus:ring-red-200 focus:border-red-500"
                        : "border-gray-200")
                    }
                  />
                  {cwTouched.name && cwErrors.name ? (
                    <p
                      id="cw_name_error"
                      className="mt-2 text-red-600 text-base"
                    >
                      {cwErrors.name}
                    </p>
                  ) : null}
                </div>

                {/* Phone */}
                <div style={makeFadeUp(connectInView, 210, 10, 720)}>
                  <label htmlFor="cw_phone" className="block text-lg font-semibold text-black mb-2">
                    Your Phone <span className="text-red-600">*</span>
                  </label>
                  <input
                    id="cw_phone"
                    type="tel"
                    name="cw_phone"
                    autoComplete="tel"
                    inputMode="tel"
                    value={cwPhone}
                    onChange={(e) => {
                      setCwPhone(e.target.value);
                      if (cwSubmitState.status !== "idle") {
                        setCwSubmitState({ status: "idle", banner: "" });
                      }
                    }}
                    onBlur={() => setCwTouched((t) => ({ ...t, phone: true }))}
                    placeholder="Your Phone"
                    aria-invalid={cwTouched.phone && !!cwErrors.phone}
                    aria-describedby="cw_phone_error"
                    className={
                      "w-full h-[52px] px-5 rounded-[4px] border text-lg text-gray-700 placeholder:text-gray-400 " +
                      "focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-sky-300 " +
                      (cwTouched.phone && cwErrors.phone
                        ? "border-red-500 focus:ring-red-200 focus:border-red-500"
                        : "border-gray-200")
                    }
                  />
                  {cwTouched.phone && cwErrors.phone ? (
                    <p
                      id="cw_phone_error"
                      className="mt-2 text-red-600 text-base"
                    >
                      {cwErrors.phone}
                    </p>
                  ) : null}
                </div>

                {/* Email */}
                <div style={makeFadeUp(connectInView, 260, 10, 720)}>
                  <label htmlFor="cw_email" className="block text-lg font-semibold text-black mb-2">
                    Your Email <span className="text-red-600">*</span>
                  </label>
                  <input
                    id="cw_email"
                    type="email"
                    name="cw_email"
                    autoComplete="email"
                    inputMode="email"
                    value={cwEmail}
                    onChange={(e) => {
                      setCwEmail(e.target.value);
                      if (cwSubmitState.status !== "idle") {
                        setCwSubmitState({ status: "idle", banner: "" });
                      }
                    }}
                    onBlur={() => setCwTouched((t) => ({ ...t, email: true }))}
                    placeholder="Your Email"
                    aria-invalid={cwTouched.email && !!cwErrors.email}
                    aria-describedby="cw_email_error"
                    className={
                      "w-full h-[52px] px-5 rounded-[4px] border text-lg text-gray-700 placeholder:text-gray-400 " +
                      "focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-sky-300 " +
                      (cwTouched.email && cwErrors.email
                        ? "border-red-500 focus:ring-red-200 focus:border-red-500"
                        : "border-gray-200")
                    }
                  />
                  {cwTouched.email && cwErrors.email ? (
                    <p
                      id="cw_email_error"
                      className="mt-2 text-red-600 text-base"
                    >
                      {cwErrors.email}
                    </p>
                  ) : null}
                </div>

                {/* Message */}
                <div style={makeFadeUp(connectInView, 310, 10, 720)}>
                  <label htmlFor="cw_message" className="block text-lg font-semibold text-black mb-2">
                    Your Message
                  </label>
                  <textarea
                    id="cw_message"
                    name="cw_message"
                    autoComplete="off"
                    value={cwMessage}
                    onChange={(e) => setCwMessage(e.target.value)}
                    onBlur={() =>
                      setCwTouched((t) => ({ ...t, message: true }))
                    }
                    placeholder=""
                    rows={6}
                    className="w-full px-5 py-4 rounded-[4px] border border-[#2a3a57]
                               text-lg text-gray-700 placeholder:text-gray-400
                               focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-sky-300"
                  />
                </div>

                {/* Send button */}
                <button
                  type="submit"
                  disabled={cwSubmitState.status === "submitting"}
                  className="mt-2 inline-flex items-center justify-center
                             bg-[#20a7df] hover:bg-sky-600 text-white font-bold
                             w-[220px] h-[62px] rounded-full text-xl
                             shadow-[0_12px_26px_rgba(0,0,0,0.16)]
                             transition disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{
                    ...makeFadeUp(connectInView, 360, 10, 720),
                    willChange: "transform",
                    transition: `opacity ${dur(720)}ms ${ease} 360ms, transform ${dur(520)}ms ${ease}, background-color 300ms`,
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.transform = "translateY(-2px)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.transform = "translateY(0px)")
                  }
                >
                  {cwSubmitState.status === "submitting"
                    ? "SENDING..."
                    : "SEND"}
                </button>

                {/* Error banner */}
                {cwSubmitState.status === "error" && cwSubmitState.banner ? (
                  <div
                    style={makeFadeUp(connectInView, 420, 10, 720)}
                    className="mt-6 border-2 border-[#f59e0b] bg-white
                               px-6 py-4 text-lg text-gray-800"
                  >
                    {cwSubmitState.banner}
                  </div>
                ) : null}

                {/* Success banner */}
                {cwSubmitState.status === "success" && cwSubmitState.banner ? (
                  <div
                    style={makeFadeUp(connectInView, 420, 10, 720)}
                    className="mt-6 border-2 border-[#22c55e] bg-white
                               px-6 py-4 text-lg text-gray-800"
                  >
                    {cwSubmitState.banner}
                  </div>
                ) : null}
              </form>
            </div>

            {/* RIGHT: DETAILS + MAP */}
            <div
              style={makeFadeRight(connectInView, 120, 18, 760)}
              className="w-full max-w-[820px] mx-auto lg:mx-0"
            >
              {/* Opening Hours box */}
              <div
                style={makeFadeUp(connectInView, 150, 10, 720)}
                className="bg-gradient-to-b from-[#20a7df] to-[#49628b]
                           text-white text-center
                           py-6 px-6
                           shadow-[0_14px_34px_rgba(0,0,0,0.22)]
                           rounded-[2px]"
              >
                <div className="text-3xl sm:text-4xl font-bold drop-shadow">
                  Opening Hours
                </div>
              </div>

              <div
                style={makeFadeUp(connectInView, 210, 10, 720)}
                className="mt-6 text-xl text-gray-800"
              >
                <span className="font-semibold">Monday to Friday</span> –
                10:00AM to 6:00PM
              </div>

              {/* Address box */}
              <div
                style={makeFadeUp(connectInView, 260, 10, 720)}
                className="mt-8 bg-gradient-to-b from-[#20a7df] to-[#49628b]
                           text-white text-center
                           py-6 px-6
                           shadow-[0_14px_34px_rgba(0,0,0,0.22)]
                           rounded-[2px]"
              >
                <div className="text-3xl sm:text-4xl font-bold drop-shadow">
                  Address
                </div>
              </div>

              <div
                style={makeFadeUp(connectInView, 310, 10, 720)}
                className="mt-6 space-y-4 text-xl text-gray-800"
              >
                <div>
                  <span className="font-semibold">Ontario:</span> 9300 Goreway
                  Drive, Unit #106, Brampton, ON L6P 4N1
                </div>
                <div>
                  <span className="font-semibold">Alberta:</span> 9426 51 Avenue
                  NW,Suite 201, Edmonton, T6E 5A6
                </div>
              </div>

              {/* Map */}
              <div
                style={makeFadeScale(connectInView, 360, 0.99, 780)}
                className="mt-8 w-full rounded-[6px] overflow-hidden shadow-[0_16px_36px_rgba(0,0,0,0.18)] border border-gray-200"
              >
                <iframe
                  title="Google Map - Ontario Office"
                  src="https://www.google.com/maps?q=9300%20Goreway%20Dr%20%23106%2C%20Brampton%2C%20ON%20L6P%204N1&z=12&output=embed"
                  className="w-full h-[360px] md:h-[380px] border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>
      <BlogSection title="Latest Blogs" mode="related" relatedCount={3} />
    </div>
  );
}

export default Home;
